<template>
  <v-card-text>
    <v-container v-if="previewItem" fluid>
      <v-row>
        <!-- Left Profile Section -->
        <v-col cols="12" md="3" class="pr-md-4">
          <v-card flat class="text-center text-md-left">
            <v-avatar size="100" class="mb-3" color="primary lighten-3">
              <img :src="authorAvatar" @error="handleAvatarError" style="object-fit: cover; width: 100%; height: 100%;">
            </v-avatar>

            <div class="text-h6">{{ previewItem.SubmittedByName || 'Author' }}</div>
            <div class="text-caption grey--text mb-3">
              {{ formattedDate }}
            </div>

            <v-divider class="my-2"></v-divider>

            <div class="d-flex justify-center justify-md-start">
              <div class="stat-item mr-4">
                <v-icon small color="grey">mdi-eye</v-icon>
                <span class="ml-1">{{ previewItem.Views || 0 }}</span>
              </div>
              <div class="stat-item">
                <v-icon small color="grey">mdi-account-group</v-icon>
                <span class="ml-1">{{ previewItem.Followers || 0 }}</span>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Right Content Section -->
        <v-col cols="12" md="9">
          <div class="category-tags mb-3">
            <span class="category-badge">{{ previewItem.CategoryName || 'Public' }}</span>
            <v-icon x-small class="mx-1">mdi-chevron-right</v-icon>
            <span class="category-badge">{{ previewItem.SubCategoryName || 'Communities' }}</span>
          </div>

          <h2 class="text-h5 mb-3">{{ previewItem.Title || 'Untitled Post' }}</h2>

          <div class="content-body mb-4" v-html="previewItem.Description"></div>

          <!-- Media Display -->
          <div class="media-container mb-4">
            <template v-if="previewItem.ImgVid">
              <v-img v-if="isImage" :src="previewItem.ImgVid" max-height="400" contain @error="handleMediaError" />
              <video v-else-if="isVideo" controls style="max-height: 400px; width: 100%;" @error="handleMediaError">
                <source :src="previewItem.ImgVid" :type="videoType">
              </video>
              <v-alert v-else type="warning" dense outlined>
                Unsupported media type
              </v-alert>
            </template>
            <v-alert v-else type="info" dense outlined>
              No media attachment
            </v-alert>
          </div>

          <!-- Engagement Stats -->
          <v-divider></v-divider>
          <div class="engagement-stats mt-3">
            <div class="stat-item mr-4">
              <v-icon small color="grey">mdi-comment-outline</v-icon>
              <span class="ml-1">{{ previewItem.Comments || 0 }} comments</span>
            </div>
            <div class="stat-item">
              <v-icon small color="grey">mdi-heart-outline</v-icon>
              <span class="ml-1">{{ previewItem.Likes || 0 }} likes</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-card-text>
</template>

<script>
export default {
  props: {
    previewItem: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      defaultAvatar: 'https://cdn.vuetifyjs.com/images/profiles/md5.png',
      avatarError: false,
      mediaLoadError: false,
      imageExtensions: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'],
      videoExtensions: ['mp4', 'webm', 'ogg', 'mov']
    }
  },
  computed: {
    authorAvatar() {
      return this.avatarError ? this.defaultAvatar :
        (this.previewItem.SubmittedByProfileImage || this.defaultAvatar);
    },
    formattedDate() {
      return this.previewItem.CreatedDate ?
        new Date(this.previewItem.CreatedDate).toLocaleDateString() : 'No date'
    },
    isImage() {
      if (!this.previewItem.ImgVid || this.mediaLoadError) return false
      const ext = this.getFileExtension(this.previewItem.ImgVid)
      return this.imageExtensions.includes(ext)
    },
    isVideo() {
      if (!this.previewItem.ImgVid || this.mediaLoadError) return false
      const ext = this.getFileExtension(this.previewItem.ImgVid)
      return this.videoExtensions.includes(ext)
    },
    videoType() {
      if (!this.previewItem.ImgVid) return ''
      const ext = this.getFileExtension(this.previewItem.ImgVid)
      return `video/${ext}`
    }
  },
  methods: {
    handleAvatarError(event) {
      this.avatarError = true;
      event.target.src = this.defaultAvatar;
    },
    handleMediaError() {
      this.mediaLoadError = true
    },
    getFileExtension(url) {
      if (!url) return ''
      const cleanUrl = url.split('?')[0]
      return cleanUrl.split('.').pop().toLowerCase()
    }
  }
}
</script>

<style scoped>
.category-tags {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.category-badge {
  background-color: #e3f2fd;
  padding: 2px 8px;
  border-radius: 4px;
  color: #1976d2;
}

.content-body {
  line-height: 1.6;
}

.content-body>>>img {
  max-width: 100%;
  height: auto;
}

.media-container {
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #eee;
  background: #f5f5f5;
  min-height: 60px;
}

.v-avatar {
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-item {
  display: inline-flex;
  align-items: center;
  color: #616161;
  font-size: 0.9rem;
}

.engagement-stats {
  display: flex;
  align-items: center;
}

@media (max-width: 960px) {
  .profile-section {
    flex-direction: row;
    align-items: center;
    margin-bottom: 16px;
  }

  .profile-section .v-avatar {
    margin-right: 16px;
    margin-bottom: 0;
  }
}
</style>